import React, { useState } from 'react';
import { UploadFileList } from './UploadFileList';
import './uploadField.scss';
import { checkFiles } from '../../_helper/fileHelper';
import { OnSpotFileData, FileUploadType } from '@/model/onSpot/OnSpot';
import { Icon } from '@/comp/icon/icon';
import { Tooltip } from '@/comp/toolTip/tooltip';

interface UploadFieldProps {
    uniqueKey: string;
    category: number;
    subCategory?: number;
    uploadType: FileUploadType;
    setUploadFiles: (id: string, fileList: OnSpotFileData[]) => void;
    fileData: OnSpotFileData[];
}

export const UploadField = ({
    uniqueKey,
    category,
    subCategory,
    uploadType,
    setUploadFiles,
    fileData,
}: UploadFieldProps) => {
    const [fileErrors, setFileErrors] = useState<string[]>();
    const [isHovered, setIsHovered] = useState(false);

    const uploadForm = {
        fault: 'Mängelprotokoll',
        check24: 'Einzelverbindungsnachweis zu CHECK24',
        luggage: 'Mängelprotokoll/PIR der Fluggesellschaft',
        general: 'Fotos, Videos, Rechnungen, Zahlungsbelege',
    };

    const uploadDescription = {
        fault: 'Für gewöhnlich ist das Mängelprotokoll ein A4-Zettel, auf dem bestimmte Daten wie Name und Adresse der Unterkunft, Erstellungsdatum und Mängel protokolliert werden. Eine bestimmte Form ist für das Mängelprotokoll nicht vorgeschrieben. Wichtig ist, dass alles vollständig sowie detailliert und unmissverständlich formuliert ist.',
        check24:
            'Bei einem Einzelverbindungsnachweis handelt es sich um eine aufgeschlüsselte Übersicht aller Ihrer getätigten Anrufe und SMS. Diese Informationen können Sie bei Ihrem Telefonanbieter einholen.',
        luggage:
            'Kommt es am Flughafen zu Beschädigung oder Verlust des Gepäcks, sind Sie angehalten, dies umgehend am Lost & Found-Schalter zu melden. Dort erhalten Sie als Bestätigung eine Referenznummer (PIR = Property Irregularity Report). Diese Referenznummer ist zwingend notwendig, um den Bearbeitungsstand der Gepäckunregelmäßigkeit international verfolgen zu können.',
        general:
            'Als Zahlungsnachweis werden sowohl Kassenbons, Bestätigungen einer durchgeführten Überweisung, Kreditkartenabrechnungen und andere Belegarten anerkannt.',
    };

    const addFiles = async (list: FileList, elem: HTMLInputElement) => {
        let newFileList: OnSpotFileData[] = [...fileData];
        let fileErrors: string[] = [];
        for (const file of Array.from(list)) {
            try {
                const newFile = await checkFiles(file);
                if (newFile === 'success') {
                    newFileList.push({
                        file: file,
                        category: category,
                        subCategory: subCategory,
                        documentType: uploadType,
                    });
                }
            } catch (error) {
                if (error instanceof Error) {
                    fileErrors.push(error.message);
                }
            }
        }
        if (fileErrors.length > 0) {
            setFileErrors(fileErrors);
        } else {
            if (newFileList.length > 0) {
                setUploadFiles(elem.id, newFileList);
            }
        }
    };

    const changeFile = (e: React.ChangeEvent<HTMLInputElement>) => {
        e.preventDefault();

        if (e.target.files) {
            addFiles(e.target.files, e.target).then();
        }
    };

    const dropHandler = (e: React.DragEvent) => {
        e.preventDefault();
        if (e.target instanceof window.HTMLElement) {
            e.target.classList.remove('dragOver');
        }

        // Prevent default behavior (Prevent file from being opened)
        const elem = e.currentTarget.querySelector('input');
        if (elem) {
            addFiles(e.dataTransfer.files, elem).then();
        }
    };

    const dragOverHandler = (e: React.DragEvent) => {
        // Prevent default behavior (Prevent file from being opened)
        e.preventDefault();

        if (e.target instanceof HTMLElement && !e.target.classList.contains('dragOver')) {
            e.target.classList.add('dragOver');
        }
    };

    const dragLeaveHandler = (e: React.DragEvent) => {
        if (e.target instanceof HTMLElement && e.target.classList.contains('dragOver')) {
            e.target.classList.remove('dragOver');
        }
    };

    const removeFile = (index: number) => {
        if (fileData.length === 1) {
            fileData = [];
        } else {
            fileData.splice(index, 1);
        }
        setUploadFiles(uniqueKey, fileData);
    };

    return (
        <>
            <div className="upload-container" key={uniqueKey}>
                <div className="upload-title">
                    <div className="heading">
                        <div className="text-bold">
                            {uploadForm[uploadType]}
                            {uploadDescription[uploadType] && (
                                <Tooltip iconSize={24} classList="info-layer">
                                    {uploadDescription[uploadType]}
                                </Tooltip>
                            )}
                        </div>
                    </div>
                    <p className="enabled-files">Erlaubte Dateitypen: .pdf, .jpg, .png, .mp4, .mov</p>
                </div>
                <div className="upload-field-container">
                    <label
                        className="upload-field"
                        onDrop={dropHandler}
                        onMouseEnter={() => setIsHovered(true)}
                        onMouseLeave={() => setIsHovered(false)}
                        onDragOver={dragOverHandler}
                        onDragLeave={dragLeaveHandler}>
                        <Icon
                            name={isHovered ? 'fileUploadHover' : 'fileUpload'}
                            size={isHovered ? 39 : 37}
                            className="color-primary icon-upload-file"
                        />
                        <p>Nachweise für Upload auswählen</p>
                        <input onChange={changeFile} id={uniqueKey} type="file" name={uniqueKey} multiple />
                    </label>
                    {fileData.length > 0 && (
                        <div className="upload-files-list">
                            <UploadFileList fileList={fileData} setRemoveFile={removeFile} />
                        </div>
                    )}
                </div>
            </div>
            {fileErrors &&
                fileErrors.map((error) => (
                    <div className="error text-red" key={error}>
                        {error}
                    </div>
                ))}
        </>
    );
};
export default UploadField;
